/**
 * @fileoverview Company and Accelerator Display Components
 * @active Used in current Next.js App Router routes for company and investor profiles
 *
 * This file provides two main components:
 * 1. AcceleratorDetailPage - Displays accelerator logo and cohort information
 * 2. CompanyAcceleratorChevron - Displays company information in chevron-style UI (HQ, founding year, accelerator)
 *
 * These components work together to provide consistent UI elements for company and investor profiles.
 *
 * @see {@link src/components/companies/company_Id/Company_Id.tsx} - Company detail page using these components
 * @see {@link src/components/assetManagerComponent/AssetManagerComponent.tsx} - Asset Manager page using these components
 * @see {@link src/types/company.ts} - Type definitions for company and profile data
 * @see {@link src/utils/addressSelector.ts} - Utility for processing address data
 * @see {@link src/utils/headersTableInDetails.ts} - Tab configuration for details pages
 */

import React, { Fragment, type ReactNode } from "react";

import { Box, Flex, Text, type FlexProps } from "@chakra-ui/react";
import { usePathname } from "next/navigation";

// ALERT: THIS LOOKS LIKE A PROBLEM! YOU CAN'T DISPLAY THIS WITHOUT THE SVG ICONS!
import Geo from "@/assets/svg/geo.svg";
import { acceleratorSelector } from "@/components/ui/buttons/acceleratorSelector";
import { h_400_12_135_48, h_400_14_135_28 } from "@/styles/fontStyles";
import { addressSelector } from "@/utils/addressSelector";
import {
  type CompanyChevronData,
  type ProfileChevronData,
} from "@/types/company";

/**
 * Props for the AcceleratorDetailPage component
 */
interface AcceleratorDetailPageProps {
  /**
   * Company data containing accelerator information
   */
  company: CompanyChevronData;

  /**
   * Whether to display as a block
   * @default false
   */
  isBlock?: boolean;

  /**
   * Optional custom styles for the container
   */
  containerStyle?: FlexProps;

  /**
   * Optional custom styles for the text
   */
  textStyle?: Record<string, any>;

  /**
   * Optional children to render after the accelerator
   */
  children?: ReactNode;
}

/**
 * Active accelerator component that displays accelerator information in company detail pages
 * Used in the /companies/<company_slug> detail pages
 *
 * @param {AcceleratorDetailPageProps} props - Component props
 * @returns {JSX.Element|null} Rendered component or null if no accelerator data
 */
export const AcceleratorDetailPage = ({
  company,
  isBlock = false,
  containerStyle = {},
  textStyle = {},
  children,
}: AcceleratorDetailPageProps) => {
  // Return null if no accelerator data
  if (!company?.accelerator && !company?.initialData?.accelerator) {
    return null;
  }

  // Debug the incoming data structure
  console.log("AcceleratorDetailPage received company:", {
    companyId: company?.id,
    accelerator: company?.accelerator,
    acceleratorInInitialData: company?.initialData?.accelerator,
    acceleratorType: company?.accelerator
      ? Array.isArray(company.accelerator)
        ? "array"
        : typeof company.accelerator
      : "undefined",
    keys: company?.accelerator ? Object.keys(company.accelerator) : [],
    incubator_name: company?.accelerator?.incubator_name,
    slug: company?.accelerator?.slug,
    incubator_cohort: company?.accelerator?.incubator_cohort,
  });

  // Prepare the company data in the format expected by acceleratorSelector
  // The key is to use the correct structure that acceleratorSelector expects
  const companyData = {
    initialData: {
      accelerator: company?.initialData?.accelerator || company?.accelerator,
      webUrl: company?.initialData?.webUrl || company?.webUrl,
    },
  };

  // Get the accelerator icon with debug logging
  console.log("Calling acceleratorSelector with:", companyData);
  // Use type assertion to fix TypeScript error
  const accelerator = acceleratorSelector({ company: companyData as any });
  console.log(
    "acceleratorSelector returned:",
    accelerator ? "Icon component" : "null"
  );

  // Define the style props with proper typing for Chakra UI
  const styleFlexWrapper: FlexProps = {
    px: "8px",
    minH: "fit-content",
    w: "fit-content",
    columnGap: "8px",
    h: "32px",
    mb: "8px",
    bgColor: "white",
    borderRadius: "4px",
    filter: "drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.1))",
    align: "center",
    position: "relative" as const, // Type assertion to fix position type
    top: "0",
    left: "0",
    zIndex: "1",
    ...containerStyle,
  };

  return (
    <Fragment>
      {accelerator && (
        <Flex {...styleFlexWrapper}>
          <Box>{accelerator}</Box>
          <Text {...h_400_12_135_48} {...textStyle}>
            {company?.accelerator?.incubator_name || company?.accelerator?.slug}
          </Text>
          {company?.accelerator?.incubator_cohort && (
            <>
              <Text {...h_400_12_135_48} {...textStyle}>
                •
              </Text>
              <Text {...h_400_12_135_48} {...textStyle}>
                {company?.accelerator?.incubator_cohort}
              </Text>
            </>
          )}
          {children}
        </Flex>
      )}
    </Fragment>
  );
};

/**
 * Company Accelerator Chevron component for displaying company information with chevron-style badges
 * Used in the /companies/<company_slug> and /investors/firms/<asset-manager> detail pages
 *
 * Displays key metadata about a company in attractive, consistent chevron-style UI elements, including:
 * - Headquarters location
 * - Founding year
 * - Accelerator participation (if applicable)
 *
 * @param {Object} props - Component props
 * @param {CompanyChevronData} props.item - Company data to display in chevrons
 * @param {ProfileChevronData} [props.profile] - Optional profile data
 * @returns {JSX.Element} Rendered component
 *
 * @see {@link src/components/assetManagerComponent/AssetManagerComponent.tsx} - Used in Asset Manager component
 * @see {@link src/components/companies/company_Id/Company_Id.tsx} - Used in Company detail page
 * @see {@link src/types/company.ts} - Type definitions for company and profile data
 */
export function CompanyAcceleratorChevron({
  item,
  profile,
}: {
  item: CompanyChevronData;
  profile?: ProfileChevronData | null;
}) {
  const pathName = usePathname();
  const address = addressSelector({ data: item?.addresses });

  return (
    <Flex
      flexDirection={{ base: "column", smx: "row" }}
      alignItems={{ base: "center", smx: "start" }}
      gap={"9px"}
    >
      {address && address !== "—" ? (
        <Flex
          bgColor={"white"}
          alignItems={"center"}
          w={"100%"}
          width="fit-content"
          pr={"30px"}
          borderRadius={"4px"}
          boxShadow={"0px 0px 14px 0px rgba(114, 114, 114, 0.25)"}
        >
          <Flex
            p={!profile ? "8px 20px 8px 20px" : "0 20px"}
            {...h_400_14_135_28}
            color={"dashboardBackground"}
            bgColor={"mainBlue"}
            alignItems={"center"}
            borderLeftRadius={"4px"}
            h={"30px"}
            clipPath={
              "polygon(0 0, calc(100% - 14px) 0, 100% 50%, calc(100% - 14px) 100%, 0 100%, 0 0)"
            }
          >
            <Text mr={"14px"} pt={!profile ? "2px" : "0px"}>
              {profile ? "📍" : "HQ"}
            </Text>
          </Flex>
          <Text
            as={"h2"}
            ml={"20px"}
            {...h_400_14_135_28}
            color={"mainBLue"}
            pt={"3px"}
            whiteSpace={"nowrap"}
            isTruncated
          >
            {address as React.ReactNode}
          </Text>
        </Flex>
      ) : null}

      {item?.dateYearEstablished && item?.dateYearEstablished !== "" ? (
        <Flex
          bgColor={"white"}
          alignItems={"center"}
          w={"100%"}
          width="fit-content"
          pr={"30px"}
          borderRadius={"4px"}
          boxShadow={"0px 0px 14px 0px rgba(114, 114, 114, 0.25)"}
        >
          <Flex
            p={"8px 20px 5px 20px"}
            {...h_400_14_135_28}
            color={"dashboardBackground"}
            bgColor={"mainBlue"}
            alignItems={"center"}
            borderLeftRadius={"4px"}
            h={"30px"}
            clipPath={
              "polygon(0 0, calc(100% - 14px) 0, 100% 50%, calc(100% - 14px) 100%, 0 100%, 0 0)"
            }
          >
            <Text mr={"14px"}>Founded </Text>
          </Flex>
          <Text ml={"20px"} {...h_400_14_135_28} color={"mainBlue"} pt={"3px"}>
            {item?.dateYearEstablished || "—"}
          </Text>
        </Flex>
      ) : null}

      {item?.accelerator &&
      Object.keys(item.accelerator).length &&
      !pathName?.includes("/people") ? (
        <AcceleratorDetailPage {...{ company: item, isBlock: true }} />
      ) : null}
    </Flex>
  );
}

// For backward compatibility with existing imports
export const AcceleratorMobile = AcceleratorDetailPage;
export const ProfileChevron = CompanyAcceleratorChevron; // Keep this for backward compatibility

export default AcceleratorDetailPage;
